<script setup lang="ts">
/**
 * Приглашение на мит-сессию (DEV-1947).
 *
 * Висит в каркасе, а не на плане: зовут человека там, где он есть, а он в этот
 * момент смотрит доску или задачу. Баннер на одном экране плана увидел бы
 * только тот, кто и так собирался писать заметки.
 *
 * Опрос раз в десять секунд — приглашение приходит редко, и держать ради него
 * живой поток в каждой вкладке дороже, чем один лёгкий запрос: поток встречи
 * открывается уже внутри мит-режима.
 */
import { useIntervalFn } from '@vueuse/core'
import { Users } from 'lucide-vue-next'
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { useAuthStore } from '@/features/auth/store'
import { useMeetingStore } from '@/features/plan/meetingStore'

/** Как часто спрашиваем про приглашения */
const POLL_INTERVAL = 10_000

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const meeting = useMeetingStore()

/** Первое приглашение: их редко бывает больше одного разом */
const invite = computed(() => meeting.invites[0] ?? null)

/**
 * Показывать ли баннер. На самом плане молчим: там встреча и так на экране, а
 * полоса поверх неё дублировала бы то, что человек уже видит.
 */
const isVisible = computed(
  () => auth.isAuthenticated && !!invite.value && route.name !== 'plan',
)

const join = async () => {
  const pending = invite.value

  if (!pending) return

  await meeting.join(pending.sessionId)
  await router.push({ name: 'plan' })
}

onMounted(() => {
  if (auth.isAuthenticated) void meeting.refreshState()
})

useIntervalFn(() => {
  if (auth.isAuthenticated) void meeting.refreshState()
}, POLL_INTERVAL)
</script>

<template>
  <div v-if="isVisible" class="invite" role="status">
    <Users :size="16" aria-hidden="true" />

    <span class="invite__text">
      {{ t('plan.meeting.banner', { name: invite?.invitedByName || t('plan.meeting.someone') }) }}
    </span>

    <button type="button" class="invite__join" @click="join()">
      {{ t('plan.meeting.join') }}
    </button>
  </div>
</template>

<style scoped>
.invite {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-primary-tint);
  color: var(--color-primary);
}

.invite__text {
  font-size: var(--font-size-body-sm);
  line-height: 18px;
}

.invite__join {
  margin-left: auto;
  padding: 4px 12px;
  border: none;
  border-radius: 6px;
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-family: inherit;
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
  cursor: pointer;
}
</style>
